/**
 * RuntimeCaps — WebGPU feature + limits detection for the renderer.
 *
 * Probes the adapter's feature set at startup and exposes it as a singleton
 * that downstream systems (ClusteredLighting, GPUDriven, SplatSort, etc.)
 * can branch on to enable fast paths where available, with CPU/WebGL
 * fallbacks where the feature isn't present.
 *
 * Features tracked:
 *   - shader-f16         — half-precision float in shaders (memory + ALU win)
 *   - subgroups          — wave intrinsics (ballot, broadcast, reductions)
 *   - subgroups-f16      — subgroup ops with f16 values
 *   - timestamp-query    — per-pass GPU timing
 *   - indirect-first-instance — GPU-generated instance offsets
 *   - float32-filterable — hardware-filtered f32 textures
 *   - depth-clip-control — reverse-Z / infinite-far clip
 *   - rg11b10ufloat-renderable / bgra8unorm-storage — efficient framebuffer formats
 *
 * This module is rendering-only. For XR-specific capabilities see
 * src/xr/XRCapabilities2.ts.
 */

import { Logger } from '../core/Logger.js';

// ─── Feature taxonomy ────────────────────────────────────────────────────────

/** WebGPU feature string names the engine tracks. Mirrors GPUFeatureName. */
export type WebGPUFeatureName =
  | 'shader-f16'
  | 'subgroups'
  | 'subgroups-f16'
  | 'timestamp-query'
  | 'indirect-first-instance'
  | 'float32-filterable'
  | 'depth-clip-control'
  | 'depth32float-stencil8'
  | 'rg11b10ufloat-renderable'
  | 'bgra8unorm-storage'
  | 'texture-compression-bc'
  | 'texture-compression-etc2'
  | 'texture-compression-astc'
  | 'clip-distances'
  | 'dual-source-blending';

/** Subset of GPUSupportedLimits we care about for tuning compute dispatches. */
export interface RelevantLimits {
  maxComputeWorkgroupSizeX: number;
  maxComputeWorkgroupSizeY: number;
  maxComputeWorkgroupSizeZ: number;
  maxComputeInvocationsPerWorkgroup: number;
  maxComputeWorkgroupsPerDimension: number;
  maxStorageBufferBindingSize: number;
  maxStorageTexturesPerShaderStage: number;
  maxUniformBufferBindingSize: number;
  maxBindGroups: number;
  maxBindingsPerBindGroup: number;
  maxTextureDimension2D: number;
  maxTextureDimension3D: number;
  maxTextureArrayLayers: number;
  maxColorAttachments: number;
  maxColorAttachmentBytesPerSample: number;
  minSubgroupSize?: number;
  maxSubgroupSize?: number;
}

export interface RuntimeGpuCaps {
  /** Whether WebGPU is available at all. */
  hasWebGPU: boolean;
  /** Graphics backend actually in use. */
  backend: 'webgpu' | 'webgl2';
  /** Full feature set reported by the GPUAdapter. */
  features: Set<WebGPUFeatureName>;
  /** Relevant limits reported by the GPUAdapter. */
  limits: RelevantLimits;
  /** Adapter info (vendor, architecture, device, description) when available. */
  adapterInfo: {
    vendor: string;
    architecture: string;
    device: string;
    description: string;
  };
  /** True if the adapter flags discrete / integrated / cpu. */
  adapterType: 'discrete' | 'integrated' | 'cpu' | 'unknown';

  // ── Convenience flags (derived from features above). ─────────────────────
  /** Shader f16 (half-precision) is usable. */
  hasF16: boolean;
  /** Subgroup (wave intrinsic) ops usable. */
  hasSubgroups: boolean;
  /** Subgroup ops with f16 values usable. */
  hasSubgroupsF16: boolean;
  /** Per-pass GPU timestamp queries. */
  hasTimestampQuery: boolean;
  /** GPU-generated indirect first-instance. */
  hasIndirectFirstInstance: boolean;
  /** Hardware-filterable f32 textures. */
  hasF32Filterable: boolean;
  /** rg11b10ufloat renderable (efficient HDR buffer). */
  hasR11G11B10RenderTarget: boolean;
  /** BC texture compression (desktop). */
  hasBC: boolean;
  /** ASTC texture compression (mobile). */
  hasASTC: boolean;
  /** ETC2 texture compression (Android). */
  hasETC2: boolean;
}

// ─── Defaults ────────────────────────────────────────────────────────────────

const DEFAULT_LIMITS: RelevantLimits = {
  maxComputeWorkgroupSizeX: 256,
  maxComputeWorkgroupSizeY: 256,
  maxComputeWorkgroupSizeZ: 64,
  maxComputeInvocationsPerWorkgroup: 256,
  maxComputeWorkgroupsPerDimension: 65535,
  maxStorageBufferBindingSize: 128 * 1024 * 1024,
  maxStorageTexturesPerShaderStage: 4,
  maxUniformBufferBindingSize: 64 * 1024,
  maxBindGroups: 4,
  maxBindingsPerBindGroup: 640,
  maxTextureDimension2D: 8192,
  maxTextureDimension3D: 2048,
  maxTextureArrayLayers: 256,
  maxColorAttachments: 8,
  maxColorAttachmentBytesPerSample: 32,
};

function emptyCaps(backend: 'webgpu' | 'webgl2'): RuntimeGpuCaps {
  return {
    hasWebGPU: backend === 'webgpu',
    backend,
    features: new Set(),
    limits: { ...DEFAULT_LIMITS },
    adapterInfo: { vendor: '', architecture: '', device: '', description: '' },
    adapterType: 'unknown',
    hasF16: false,
    hasSubgroups: false,
    hasSubgroupsF16: false,
    hasTimestampQuery: false,
    hasIndirectFirstInstance: false,
    hasF32Filterable: false,
    hasR11G11B10RenderTarget: false,
    hasBC: false,
    hasASTC: false,
    hasETC2: false,
  };
}

// ─── Detection ───────────────────────────────────────────────────────────────

/** Feature strings we consider interesting — drop anything else the browser reports. */
const KNOWN_FEATURES: ReadonlySet<string> = new Set<WebGPUFeatureName>([
  'shader-f16',
  'subgroups',
  'subgroups-f16',
  'timestamp-query',
  'indirect-first-instance',
  'float32-filterable',
  'depth-clip-control',
  'depth32float-stencil8',
  'rg11b10ufloat-renderable',
  'bgra8unorm-storage',
  'texture-compression-bc',
  'texture-compression-etc2',
  'texture-compression-astc',
  'clip-distances',
  'dual-source-blending',
]);

/**
 * Probe adapter from a Three.js WebGPU renderer after init() has resolved.
 *
 * Three.js exposes `renderer.backend.adapter` / `renderer.backend.device`
 * once the backend initialises. We handle the (rare) case where neither is
 * present by falling back to empty caps.
 */
export async function detectRuntimeCaps(renderer: unknown): Promise<RuntimeGpuCaps> {
  const backend: any = (renderer as any)?.backend;
  // three's own flag first — a production bundle minifies the class name (`WebGPUBackend` → `RF`), so the name test
  // alone reported every production WebGPU page as WebGL2; the name stays as the fallback for hand-rolled mocks.
  const backendName: string = backend?.constructor?.name ?? '';
  const isWebGPU = backend?.isWebGPUBackend === true || backendName === 'WebGPUBackend';

  if (!isWebGPU) {
    Logger.info('RuntimeCaps', 'WebGL2 backend — WebGPU feature tracking disabled');
    return emptyCaps('webgl2');
  }

  // Three r182 NEVER stores the adapter on the backend (it is a local in
  // WebGPUBackend.init(); only `this.device` is kept) — requiring it made
  // this probe return EMPTY caps on every WebGPU boot (dormant since the
  // caps landed; caught via the boot-log warning, 2026-08-04). The DEVICE is
  // the authority anyway: device.features are the GRANTED features and
  // device.limits the usable limits.
  const adapter: GPUAdapter | undefined = backend?.adapter;
  const device: GPUDevice | undefined = backend?.device;
  if (!device) {
    Logger.warn('RuntimeCaps', 'WebGPU backend present but device missing — using empty caps');
    return emptyCaps('webgpu');
  }

  const caps = emptyCaps('webgpu');

  // Features — device.features = granted (usable) set; adapter adds nothing
  // usable but is harmlessly unioned when present.
  for (const name of Array.from(device.features)) {
    if (KNOWN_FEATURES.has(name)) {
      caps.features.add(name as WebGPUFeatureName);
    }
  }
  if (adapter) {
    for (const name of Array.from(adapter.features)) {
      if (KNOWN_FEATURES.has(name)) {
        caps.features.add(name as WebGPUFeatureName);
      }
    }
  }

  // Limits — device.limits are what this device can actually do.
  const lim = (device.limits as any) ?? adapter?.limits;
  const readNum = (k: keyof GPUSupportedLimits, fallback: number): number => {
    const v = (lim as any)?.[k];
    return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
  };
  caps.limits = {
    maxComputeWorkgroupSizeX: readNum('maxComputeWorkgroupSizeX', DEFAULT_LIMITS.maxComputeWorkgroupSizeX),
    maxComputeWorkgroupSizeY: readNum('maxComputeWorkgroupSizeY', DEFAULT_LIMITS.maxComputeWorkgroupSizeY),
    maxComputeWorkgroupSizeZ: readNum('maxComputeWorkgroupSizeZ', DEFAULT_LIMITS.maxComputeWorkgroupSizeZ),
    maxComputeInvocationsPerWorkgroup: readNum('maxComputeInvocationsPerWorkgroup', DEFAULT_LIMITS.maxComputeInvocationsPerWorkgroup),
    maxComputeWorkgroupsPerDimension: readNum('maxComputeWorkgroupsPerDimension', DEFAULT_LIMITS.maxComputeWorkgroupsPerDimension),
    maxStorageBufferBindingSize: readNum('maxStorageBufferBindingSize', DEFAULT_LIMITS.maxStorageBufferBindingSize),
    maxStorageTexturesPerShaderStage: readNum('maxStorageTexturesPerShaderStage', DEFAULT_LIMITS.maxStorageTexturesPerShaderStage),
    maxUniformBufferBindingSize: readNum('maxUniformBufferBindingSize', DEFAULT_LIMITS.maxUniformBufferBindingSize),
    maxBindGroups: readNum('maxBindGroups', DEFAULT_LIMITS.maxBindGroups),
    maxBindingsPerBindGroup: readNum('maxBindingsPerBindGroup', DEFAULT_LIMITS.maxBindingsPerBindGroup),
    maxTextureDimension2D: readNum('maxTextureDimension2D', DEFAULT_LIMITS.maxTextureDimension2D),
    maxTextureDimension3D: readNum('maxTextureDimension3D', DEFAULT_LIMITS.maxTextureDimension3D),
    maxTextureArrayLayers: readNum('maxTextureArrayLayers', DEFAULT_LIMITS.maxTextureArrayLayers),
    maxColorAttachments: readNum('maxColorAttachments', DEFAULT_LIMITS.maxColorAttachments),
    maxColorAttachmentBytesPerSample: readNum('maxColorAttachmentBytesPerSample', DEFAULT_LIMITS.maxColorAttachmentBytesPerSample),
    minSubgroupSize: (lim as any)?.minSubgroupSize,
    maxSubgroupSize: (lim as any)?.maxSubgroupSize,
  };

  // Adapter info — GPUDevice.adapterInfo (current spec) covers the no-adapter
  // case; older paths kept for other implementations.
  try {
    const info: any = (device as any).adapterInfo
      ?? (adapter as any)?.info
      ?? (adapter && typeof (adapter as any).requestAdapterInfo === 'function'
        ? await (adapter as any).requestAdapterInfo()
        : null);
    if (info) {
      caps.adapterInfo = {
        vendor: info.vendor ?? '',
        architecture: info.architecture ?? '',
        device: info.device ?? '',
        description: info.description ?? '',
      };
    }
  } catch {
    // Some browsers gate adapter info behind origin trial / user prompt.
  }

  // Adapter type (Chrome exposes `isFallbackAdapter`; integrated/discrete comes from info heuristics.)
  if ((adapter as any)?.isFallbackAdapter ?? /swiftshader|llvmpipe|software/i.test(caps.adapterInfo.description)) {
    caps.adapterType = 'cpu';
  } else {
    const desc = (caps.adapterInfo.description + ' ' + caps.adapterInfo.device).toLowerCase();
    if (/intel|iris|uhd|hd graphics|amd radeon\s*(vega|graphics)|apple m\d/.test(desc)) {
      caps.adapterType = 'integrated';
    } else if (desc) {
      caps.adapterType = 'discrete';
    }
  }

  // Derived flags
  caps.hasF16 = caps.features.has('shader-f16');
  caps.hasSubgroups = caps.features.has('subgroups');
  caps.hasSubgroupsF16 = caps.features.has('subgroups-f16');
  caps.hasTimestampQuery = caps.features.has('timestamp-query');
  caps.hasIndirectFirstInstance = caps.features.has('indirect-first-instance');
  caps.hasF32Filterable = caps.features.has('float32-filterable');
  caps.hasR11G11B10RenderTarget = caps.features.has('rg11b10ufloat-renderable');
  caps.hasBC = caps.features.has('texture-compression-bc');
  caps.hasASTC = caps.features.has('texture-compression-astc');
  caps.hasETC2 = caps.features.has('texture-compression-etc2');

  Logger.info(
    'RuntimeCaps',
    `Backend: webgpu | f16: ${caps.hasF16} | subgroups: ${caps.hasSubgroups}` +
    ` | timestamp: ${caps.hasTimestampQuery} | adapter: ${caps.adapterInfo.description || caps.adapterType}`,
  );

  return caps;
}

// ─── Singleton accessor ──────────────────────────────────────────────────────

let _caps: RuntimeGpuCaps | null = null;

/** Set the global caps (called by EngineRenderer.create after init). */
export function setRuntimeGpuCaps(caps: RuntimeGpuCaps): void {
  _caps = caps;
}

/**
 * Get the global caps. If `detectRuntimeCaps` has not run yet (e.g. in unit
 * tests), returns an empty WebGL2 default so downstream code can still run.
 */
export function getRuntimeGpuCaps(): RuntimeGpuCaps {
  return _caps ?? emptyCaps('webgl2');
}

// ─── Feature requests for device creation ────────────────────────────────────

/**
 * Build a GPUDeviceDescriptor.requiredFeatures list that asks for every
 * feature the engine can use, filtered against what the adapter actually
 * supports. Pass the result to `adapter.requestDevice({ requiredFeatures })`
 * (done internally by Three.js's WebGPUBackend via its `requiredFeatures`
 * option).
 */
export function desiredRequiredFeatures(adapter: GPUAdapter): GPUFeatureName[] {
  const want: WebGPUFeatureName[] = [
    'shader-f16',
    'subgroups',
    'subgroups-f16',
    'timestamp-query',
    'indirect-first-instance',
    'float32-filterable',
    'depth-clip-control',
    'rg11b10ufloat-renderable',
    'bgra8unorm-storage',
    'texture-compression-bc',
    'texture-compression-etc2',
    'texture-compression-astc',
    'dual-source-blending',
  ];
  return want.filter((f) => adapter.features.has(f)) as GPUFeatureName[];
}
